import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, API } from '../lib/api';
import { printReportCard } from '../lib/printReport';
import { ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { CalendarCheck, Wallet, ChartLineUp, Files, CheckCircle, Circle, Printer } from '@phosphor-icons/react';

const Card = ({ icon: Icon, title, children, testid }) => (
  <div className="bg-white border border-slate-200 rounded-md p-4 stagger-in" data-testid={testid}>
    <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2 flex items-center gap-1.5"><Icon size={14} className="text-orange-600" />{title}</p>
    {children}
  </div>
);

export const StudentWidgets = () => {
  const [sid, setSid] = useState('');
  const { data: d } = useQuery({
    queryKey: ['my-overview', sid],
    queryFn: () => api.get('/my/overview', { params: sid ? { student_id: sid } : {} }).then((r) => r.data),
  });
  const { data: brand } = useQuery({ queryKey: ['branding'], queryFn: () => api.get('/branding').then((r) => r.data) });
  if (!d) return null;
  const att = d.attendance;
  return (
    <div className="mt-6" data-testid="student-widgets">
      {d.children.length > 1 && (
        <select data-testid="child-selector" value={sid || d.student.id} onChange={(e) => setSid(e.target.value)}
          className="border border-slate-200 rounded-md px-2 py-1.5 text-sm bg-white mb-3">
          {d.children.map((c) => <option key={c.id} value={c.id}>{`${c.name} (Grade ${c.grade})`}</option>)}
        </select>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        <Card icon={CalendarCheck} title="Attendance" testid="widget-attendance">
          <div className="flex items-baseline gap-2 mb-1">
            <span className="font-display text-3xl font-extrabold" data-testid="attendance-rate">{att.rate_30d != null ? `${att.rate_30d}%` : '—'}</span>
            <span className="text-xs text-slate-400">last 30 days · {att.present_30d}/{att.total_30d} days present</span>
          </div>
          {att.weekly.length > 0 ? (
            <ResponsiveContainer width="100%" height={120}>
              <AreaChart data={att.weekly} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => [`${v}%`, 'attendance']} />
                <Area type="monotone" dataKey="pct" stroke="#EA580C" fill="#FED7AA" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 py-4">No attendance captured yet.</p>}
        </Card>
        <Card icon={ChartLineUp} title="Marks across exams" testid="widget-marks">
          {d.marks_trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={148}>
              <LineChart data={d.marks_trend} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="exam" tick={{ fontSize: 9 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => [`${v}%`, 'average']} />
                <Line type="monotone" dataKey="avg_pct" stroke="#0F172A" strokeWidth={2} dot={{ r: 4, fill: '#EA580C' }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 py-4">Marks appear here once exam results are published.</p>}
        </Card>
        <Card icon={Wallet} title="Fees checklist" testid="widget-fees">
          <div className="flex gap-4 mb-2 text-xs">
            <span className="text-green-700 font-semibold" data-testid="fees-paid-total">Paid ₹{d.fees.total_paid.toLocaleString()}</span>
            <span className={d.fees.total_due > 0 ? 'text-rose-600 font-semibold' : 'text-slate-400'} data-testid="fees-due-total">Due ₹{d.fees.total_due.toLocaleString()}</span>
          </div>
          <div className="max-h-40 overflow-auto divide-y divide-slate-50">
            {d.fees.rows.map((f, i) => (
              <div key={i} className="flex items-center gap-2 py-1.5 text-sm">
                {(f.balance || 0) <= 0 ? <CheckCircle size={15} weight="fill" className="text-green-600 shrink-0" /> : <Circle size={15} className="text-slate-300 shrink-0" />}
                <span className="flex-1 truncate">{f.plan_name}</span>
                <span className="text-xs text-slate-400 tabular-nums">{(f.balance || 0) <= 0 ? 'paid' : `₹${(f.balance || 0).toLocaleString()} due`}</span>
              </div>
            ))}
            {d.fees.rows.length === 0 && <p className="text-sm text-slate-400 py-3">No invoices yet.</p>}
          </div>
        </Card>
        <Card icon={Files} title="My report cards" testid="widget-report-cards">
          <div className="max-h-44 overflow-auto divide-y divide-slate-50">
            {d.report_cards.map((r) => (
              <div key={r.id} className="flex items-center gap-2 py-2 text-sm" data-testid={`widget-rc-${r.id}`}>
                <span className="flex-1">{r.term}</span>
                <span className="font-display font-extrabold">{r.overall_pct}%</span>
                <button onClick={() => printReportCard(r, brand?.school_name || undefined, brand?.has_logo ? `${API}/branding/file/logo` : null)}
                  data-testid={`widget-rc-print-${r.id}`} className="text-xs border border-slate-200 rounded px-2 py-1 hover:border-slate-900 flex items-center gap-1"><Printer size={12} />Print</button>
              </div>
            ))}
            {d.report_cards.length === 0 && <p className="text-sm text-slate-400 py-3">No released report cards yet.</p>}
          </div>
        </Card>
      </div>
    </div>
  );
};
